import { UpdateOwnerInput, UpdateRiderInput, UpdateTrainerInput } from "../../API";
import { BarnPerson, FullRole } from "../../interfaces/Person";
import { Result } from "../../interfaces/Result";
import { BarnMember, Owner, Person, Rider, Trainer } from "../../models";
import { deleteBarnMember, getBarnMemberByBarnIdPersonId, getBarnMembersByBarnId } from "../barnMember/BarnMember";
import { deleteOwner, getOwnersByBarnId, updateOwner } from "../owner/Owner";
import { getPersonByPersonId } from "../person/Person";
import { formatDisplayNameForUser } from "../person/PersonNameFormat";
import { deleteRider, getRidersByBarnId, updateRider } from "../rider/Rider";
import { deleteTrainer, getTrainersByBarnId, updateTrainer } from "../trainer/Trainer";


/**
 * This function will get all the people connected to a barn, and return them in a formatted array.
 * It starts by getting all the barn members, and then gets all the riders, owners, and trainers connected to the barn.
 * 
 * @param barnId - The id of the barn to get the people for.
 * @param barnOwnerId 
 * @returns An array of BarnPerson objects.
 */
export async function getStrictBarnPeople(barnId: string, barnOwnerId?: string) {
    let formattedBarnPeople: BarnPerson[] = [];

    let creatorMap = new Map<string, string>(); //Person Id, Person Name

    // Get Barn Members
    let barnMembers: BarnMember[] = [];
    const barnMemberQuery = await getBarnMembersByBarnId(barnId);
    if (barnMemberQuery.isSuccess) {
        barnMembers = barnMemberQuery.result;
    }
    for (let i = 0; i < barnMembers.length; i++) {
        const currentBarnMember = barnMembers[i];

        let name = currentBarnMember.name;
        let personId = currentBarnMember.personId;
        let createdBy = currentBarnMember.createdBy;
        let creatorName = "";
        let permissionLevel = currentBarnMember.permissionLevel;
        let location = "";

        if (createdBy) {
            const creatorMapResult = creatorMap.get(createdBy);
            if (creatorMapResult) {
                creatorName = creatorMapResult;
            } else {
                const personResult = await getPersonByPersonId(createdBy);
                if (personResult.isSuccess) {
                    const creator: Person = personResult.result;
                    creatorName = formatDisplayNameForUser(creator);
                    creatorMap.set(createdBy, creatorName);
                } 
            }
        }

        const newBarnPerson: BarnPerson = {
            barnId: barnId,
            name: name,
            personId: personId,
            person: currentBarnMember.person,
            createdBy: createdBy || "",
            creatorName: creatorName,
            location: location,
            barnMember: currentBarnMember,
            isBarnAdmin: (permissionLevel === "admin" ? true : (permissionLevel === "member" ? false : undefined))
        };

        formattedBarnPeople.push(newBarnPerson);
    }

    // Get Riders connected to Barn
    let riders: Rider[] = [];
    const ridersQuery = await getRidersByBarnId(barnId);
    if (ridersQuery.isSuccess) {
        riders = riders.concat(ridersQuery.result);
    }
    for (let j = 0; j < riders.length; j++) {
        const rider = riders[j];

        const newFullRole: FullRole = {
            id: rider.id,
            role: "rider",
            rider: rider
        };

        const personId = rider.personId;
        let shouldCreateNewBarnPerson: boolean = true;
        if (personId) {
            const foundPersonIndex = formattedBarnPeople.findIndex(bp => bp.personId === personId);
            if (foundPersonIndex > -1) {
                // Update the BarnPerson
                shouldCreateNewBarnPerson = false;
                let foundBarnPerson = formattedBarnPeople[foundPersonIndex];
                let updatedBarnPerson: BarnPerson = {
                    ...foundBarnPerson,
                    location: rider.location,
                    fullRoles: foundBarnPerson.fullRoles ? [...foundBarnPerson.fullRoles, newFullRole] : [newFullRole]
                };
                formattedBarnPeople = [
                    ...formattedBarnPeople.slice(0, foundPersonIndex),
                    updatedBarnPerson,
                    ...formattedBarnPeople.slice(foundPersonIndex+1),
                ];
            }
        } else if (rider.name) {
            const normalizedRiderName = rider.name.toLocaleLowerCase().trim();
            const foundPersonIndex = formattedBarnPeople.findIndex(bp => bp.name.toLocaleLowerCase().trim() === normalizedRiderName);
            if (foundPersonIndex > -1) {
                // Update the BarnPerson
                shouldCreateNewBarnPerson = false;
                let foundBarnPerson = formattedBarnPeople[foundPersonIndex];
                let updatedBarnPerson: BarnPerson = {
                    ...foundBarnPerson,
                    location: rider.location,
                    fullRoles: foundBarnPerson.fullRoles ? [...foundBarnPerson.fullRoles, newFullRole] : [newFullRole]
                };
                formattedBarnPeople = [
                    ...formattedBarnPeople.slice(0, foundPersonIndex),
                    updatedBarnPerson,
                    ...formattedBarnPeople.slice(foundPersonIndex+1),
                ];
            }
        } 
        
        if (shouldCreateNewBarnPerson) {
            // Create a new BarnPerson object
            let name = rider.name;
            let personId = rider.personId;
            let createdBy = rider.createdBy;
            let creatorName = "";
            let location = rider.location;

            if (createdBy) {
                const creatorMapResult = creatorMap.get(createdBy);
                if (creatorMapResult) {
                    creatorName = creatorMapResult;
                } else {
                    const personResult = await getPersonByPersonId(createdBy);
                    if (personResult.isSuccess) {
                        const creator: Person = personResult.result;
                        creatorName = formatDisplayNameForUser(creator);
                        creatorMap.set(createdBy, creatorName);
                    } 
                }
            }
            
            const newBarnPerson: BarnPerson = {
                barnId: barnId,
                name: name,
                personId: personId,
                createdBy: createdBy || "",
                creatorName: creatorName,
                location: location,
                fullRoles: [newFullRole],
                isBarnAdmin: undefined,
                isBarnOwner: undefined
            };
            formattedBarnPeople.push(newBarnPerson);
        }
    }

    // Get Owners connected to Barn
    let owners: Owner[] = [];
    const ownersQuery = await getOwnersByBarnId(barnId);
    if (ownersQuery.isSuccess) {
        owners = owners.concat(ownersQuery.result);
    }
    for (let j = 0; j < owners.length; j++) {
        const owner = owners[j];

        const newFullRole: FullRole = {
            id: owner.id,
            role: "owner",
            owner: owner
        };

        const personId = owner.personId;
        let shouldCreateNewBarnPerson: boolean = true;
        if (personId) {
            const foundPersonIndex = formattedBarnPeople.findIndex(bp => bp.personId === personId);
            if (foundPersonIndex > -1) {
                // Update the BarnPerson
                shouldCreateNewBarnPerson = false;
                let foundBarnPerson = formattedBarnPeople[foundPersonIndex];
                let updatedBarnPerson: BarnPerson = {
                    ...foundBarnPerson,
                    location: owner.location,
                    fullRoles: foundBarnPerson.fullRoles ? [...foundBarnPerson.fullRoles, newFullRole] : [newFullRole]
                };
                formattedBarnPeople = [
                    ...formattedBarnPeople.slice(0, foundPersonIndex),
                    updatedBarnPerson,
                    ...formattedBarnPeople.slice(foundPersonIndex+1),
                ];
            }
        } else if (owner.name) {
            const normalizedOwnerName = owner.name.toLocaleLowerCase().trim();
            const foundPersonIndex = formattedBarnPeople.findIndex(bp => bp.name.toLocaleLowerCase().trim() === normalizedOwnerName);
            shouldCreateNewBarnPerson = false;
            if (foundPersonIndex > -1) {
                // Update the BarnPerson
                let foundBarnPerson = formattedBarnPeople[foundPersonIndex];
                let updatedBarnPerson: BarnPerson = {
                    ...foundBarnPerson,
                    location: owner.location,
                    fullRoles: foundBarnPerson.fullRoles ? [...foundBarnPerson.fullRoles, newFullRole] : [newFullRole]
                };
                formattedBarnPeople = [
                    ...formattedBarnPeople.slice(0, foundPersonIndex),
                    updatedBarnPerson,
                    ...formattedBarnPeople.slice(foundPersonIndex+1),
                ];
            }
        } 
        
        if (shouldCreateNewBarnPerson) {
            // Create a new BarnPerson object
            let name = owner.name;
            let personId = owner.personId;
            let createdBy = owner.createdBy;
            let creatorName = "";
            let location = owner.location;

            if (createdBy) {
                const creatorMapResult = creatorMap.get(createdBy);
                if (creatorMapResult) {
                    creatorName = creatorMapResult;
                } else {
                    const personResult = await getPersonByPersonId(createdBy);
                    if (personResult.isSuccess) {
                        const creator: Person = personResult.result;
                        creatorName = formatDisplayNameForUser(creator);
                        creatorMap.set(createdBy, creatorName);
                    } 
                }
            }
            
            const newBarnPerson: BarnPerson = {
                barnId: barnId,
                name: name,
                personId: personId,
                createdBy: createdBy || "",
                creatorName: creatorName,
                location: location,
                fullRoles: [newFullRole],
                isBarnAdmin: undefined,
                isBarnOwner: undefined
            };
            formattedBarnPeople.push(newBarnPerson);
        }
    }

    // Get Trainers connected to Barn
    let trainers: Trainer[] = [];
    const trainerQuery = await getTrainersByBarnId(barnId);
    if (trainerQuery.isSuccess) {
        trainers = trainers.concat(trainerQuery.result);
    }
    for (let j = 0; j < trainers.length; j++) {
        const trainer = trainers[j];

        const newFullRole: FullRole = {
            id: trainer.id,
            role: "trainer",
            trainer: trainer
        };

        const personId = trainer.personId;
        let shouldCreateNewBarnPerson = true;
        if (personId) {
            const foundPersonIndex = formattedBarnPeople.findIndex(bp => bp.personId === personId);
            if (foundPersonIndex > -1) {
                // Update the BarnPerson
                shouldCreateNewBarnPerson = false;
                let foundBarnPerson = formattedBarnPeople[foundPersonIndex];
                let updatedBarnPerson: BarnPerson = {
                    ...foundBarnPerson,
                    location: trainer.location,
                    fullRoles: foundBarnPerson.fullRoles ? [...foundBarnPerson.fullRoles, newFullRole] : [newFullRole]
                };
                formattedBarnPeople = [
                    ...formattedBarnPeople.slice(0, foundPersonIndex),
                    updatedBarnPerson,
                    ...formattedBarnPeople.slice(foundPersonIndex+1),
                ];
            }
        } else if (trainer.name) {
            const normalizedTrainerName = trainer.name.toLocaleLowerCase().trim();
            const foundPersonIndex = formattedBarnPeople.findIndex(bp => bp.name.toLocaleLowerCase().trim() === normalizedTrainerName);
            if (foundPersonIndex > -1) {
                // Update the BarnPerson
                shouldCreateNewBarnPerson = false;
                let foundBarnPerson = formattedBarnPeople[foundPersonIndex];
                let updatedBarnPerson: BarnPerson = {
                    ...foundBarnPerson,
                    location: trainer.location,
                    fullRoles: foundBarnPerson.fullRoles ? [...foundBarnPerson.fullRoles, newFullRole] : [newFullRole]
                };
                formattedBarnPeople = [
                    ...formattedBarnPeople.slice(0, foundPersonIndex),
                    updatedBarnPerson,
                    ...formattedBarnPeople.slice(foundPersonIndex+1),
                ];
            }
        } 
        
        if (shouldCreateNewBarnPerson) {
            // Create a new BarnPerson object
            let name = trainer.name;
            let personId = trainer.personId;
            let createdBy = trainer.createdBy;
            let creatorName = "";
            let location = trainer.location;

            if (createdBy) {
                const creatorMapResult = creatorMap.get(createdBy);
                if (creatorMapResult) {
                    creatorName = creatorMapResult;
                } else {
                    const personResult = await getPersonByPersonId(createdBy);
                    if (personResult.isSuccess) {
                        const creator: Person = personResult.result;
                        creatorName = formatDisplayNameForUser(creator);
                        creatorMap.set(createdBy, creatorName);
                    } 
                }
            }
            
            const newBarnPerson: BarnPerson = {
                barnId: barnId,
                name: name,
                personId: personId,
                createdBy: createdBy || "",
                creatorName: creatorName,
                location: location,
                fullRoles: [newFullRole],
                isBarnAdmin: undefined,
                isBarnOwner: undefined
            };
            formattedBarnPeople.push(newBarnPerson);
        }
    }

    // Get Barn Creator
    const creatorId = barnOwnerId;
    const foundBarnOwnerIndex = formattedBarnPeople.findIndex(bp => bp.personId === creatorId);
    if (foundBarnOwnerIndex > -1) {
        // Update the BarnPerson
        let foundBarnPerson = formattedBarnPeople[foundBarnOwnerIndex];
        let updatedBarnPerson: BarnPerson = {
            ...foundBarnPerson,
            isBarnOwner: true
        };
        formattedBarnPeople = [
            ...formattedBarnPeople.slice(0, foundBarnOwnerIndex),
            updatedBarnPerson,
            ...formattedBarnPeople.slice(foundBarnOwnerIndex+1),
        ];
    }

    // Get Person objects for each BarnPerson
    let updatedBarnPeople: BarnPerson[] = [];
    for (let i = 0; i < formattedBarnPeople.length; i++) {
        const currentBarnPerson = formattedBarnPeople[i];
        if (!currentBarnPerson.person && currentBarnPerson.personId) {
            const personQuery = await getPersonByPersonId(currentBarnPerson.personId);
            if (personQuery.isSuccess) {
                const updatedBarnPerson: BarnPerson = {
                    ...currentBarnPerson,
                    person: personQuery.result
                };
                updatedBarnPeople.push(updatedBarnPerson);
            } else {
                updatedBarnPeople.push(currentBarnPerson);
            }
        } else {
            updatedBarnPeople.push(currentBarnPerson);
        }
    }

    const sorted = updatedBarnPeople.sort((a, b) => a.name.localeCompare(b.name));
    return sorted;
}

export async function removeBarnPerson(barnPerson: BarnPerson, barnId: string, userId: string) {
    let result: Result = {isSuccess: true, type: "BarnPerson", result: barnPerson, message: "Successfully removed the person from the barn."};
    const isRider = barnPerson.fullRoles?.some(role => role.role === "rider");
    const isOwner = barnPerson.fullRoles?.some(role => role.role === "owner");
    const isTrainer = barnPerson.fullRoles?.some(role => role.role === "trainer");
    if (isRider) {
        const riderRoles: FullRole[] | undefined = barnPerson.fullRoles?.filter(role => role.role === "rider");
        if (riderRoles && riderRoles.length > 0) {
            for (let i = 0; i < riderRoles.length; i++) {
                const currentRiderRole: FullRole = riderRoles[i];
                const rider = currentRiderRole.rider;
                if (rider && rider.createdBy === userId) {
                    const removeRiderResult = await deleteRider({id: rider.id});
                    if (!removeRiderResult.isSuccess) {
                        result = {isSuccess: false, type: "BarnPerson", result: removeRiderResult, message: "Could not remove the person from the barn."};
                        return result;
                    }
                } else if (rider) {
                    const updateRiderInput: UpdateRiderInput = {
                        id: rider.id,
                        barnId: "",
                        barnName: ""
                    };
                    const updateRiderResult = await updateRider(updateRiderInput);
                    if (!updateRiderResult.isSuccess) {
                        result = {isSuccess: false, type: "BarnPerson", result: updateRiderResult, message: "Could not remove the person from the barn."};
                        return result;
                    }
                }
            }
        }
    }
    if (isOwner) {
        const ownerRoles: FullRole[] | undefined = barnPerson.fullRoles?.filter(role => role.role === "owner");
        if (ownerRoles && ownerRoles.length > 0) {
            for (let i = 0; i < ownerRoles.length; i++) {
                const currentOwnerRole: FullRole = ownerRoles[i];
                const owner = currentOwnerRole.owner;
                if (owner && owner.createdBy === userId) {
                    const removeOwnerResult = await deleteOwner({id: owner.id});
                    if (!removeOwnerResult.isSuccess) {
                        result = {isSuccess: false, type: "BarnPerson", result: removeOwnerResult, message: "Could not remove the person from the barn."};
                        return result;
                    }
                } else if (owner) {
                    const updateOwnerInput: UpdateOwnerInput = {
                        id: owner.id,
                        barnId: ""
                    };
                    const updateOwnerResult = await updateOwner(updateOwnerInput);
                    if (!updateOwnerResult.isSuccess) {
                        result = {isSuccess: false, type: "BarnPerson", result: updateOwnerResult, message: "Could not remove the person from the barn."};
                        return result;
                    }
                }
            }
        }
    }
    if (isTrainer) {
        const trainerRoles: FullRole[] | undefined = barnPerson.fullRoles?.filter(role => role.role === "trainer");
        if (trainerRoles && trainerRoles.length > 0) {
            for (let i = 0; i < trainerRoles.length; i++) {
                const currentTrainerRole: FullRole = trainerRoles[i];
                const trainer = currentTrainerRole.trainer;
                if (trainer && trainer.createdBy === userId) {
                    const removeTrainerResult = await deleteTrainer({id: trainer.id});
                    if (!removeTrainerResult.isSuccess) {
                        result = {isSuccess: false, type: "BarnPerson", result: removeTrainerResult, message: "Could not remove the person from the barn."};
                        return result;
                    }
                } else if (trainer) {
                    const updateTrainerInput: UpdateTrainerInput = {
                        id: trainer.id,
                        barnId: "",
                        barnName: ""
                    };
                    const updateTrainerResult = await updateTrainer(updateTrainerInput);
                    if (!updateTrainerResult.isSuccess) {
                        result = {isSuccess: false, type: "BarnPerson", result: updateTrainerResult, message: "Could not remove the person from the barn."};
                        return result;
                    }
                }
            }
        }
    }
    if (barnPerson.personId) {
        const queryResult = await getBarnMemberByBarnIdPersonId(barnId, barnPerson.personId);
        if (queryResult.isSuccess) {
            const barnMember: BarnMember = queryResult.result;
            if (barnMember) {
                const deleteResult = await deleteBarnMember({id: barnMember.id});
                if (!deleteResult.isSuccess) {
                    result = {isSuccess: false, type: "BarnPerson", result: deleteBarnMember, message: "Could not remove the person from the barn."};
                    return result;
                }
            }
        }
    }
    return result;
}